/**
 * DomainConnectDrawer — class-D host for the custom domain (DC-19, USER_JOURNEYS §7).
 * A right Drawer (not a Dialog) so the section's remaining SDialogs (the remove confirm)
 * open above it within one modal layer — never a Dialog nested inside a Dialog. Since
 * follow-up F1 the section runs in `addMode="inline"` here: the empty state renders the
 * domain setup form directly in the Drawer, so connecting a domain is ONE container
 * (drawer → input → DNS records), not drawer → Add button → modal. Content unmounts
 * on close → the section's status poller stops (S-10, AC-060).
 *
 * Structure follows the approved mock (`.design/launch-activation/mockups/activation-checklist.html`
 * `.drawer` block): header title + body hosting the live section + footer "Continue".
 * Footer action (fix round 1): while the section is in its empty state the inline form owns the
 * primary "Continue" (submit), so the footer drops to a secondary "Cancel" (same
 * `drawerClose` testid) — never two buttons labelled "Continue" doing different things. With a
 * domain present the footer is the primary "Continue" exactly as Plan C shipped it.
 *
 * The live `CustomDomainSection` already renders its own status copy and actions
 * (pending_dns "Verify Now" / DNS table, error retry/remove) — this Drawer adds
 * nothing beyond the header title and the closing footer action, so status copy is
 * never duplicated (AM-13).
 *
 * Router-free: `projectId` is a prop.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Drawer, Typography, useTheme } from '@mui/material';
import { CustomDomainSection } from '@modules/custom-domains';
import { LAUNCH_ACTIVATION_TEST_IDS } from '../testIds';

export const DOMAIN_DRAWER_WIDTH = 440;

export interface DomainConnectDrawerProps {
  projectId: string;
  open: boolean;
  onClose: () => void;
}

export const DomainConnectDrawer: React.FC<DomainConnectDrawerProps> = ({ projectId, open, onClose }) => {
  const theme = useTheme();
  /**
   * Exactly ONE primary action per state (fix round 1). In the empty state the inline setup form
   * owns the primary "Continue" (submit), so this footer must not render a second button with the
   * same label doing something else (close/discard) — it becomes a secondary "Cancel". Once a
   * domain exists the form is gone and the footer is the primary "Continue" again (Plan C).
   * Defaults to 'has-domain' so the pre-F1 chrome is what paints while the status loads.
   */
  const [sectionState, setSectionState] = useState<'empty' | 'has-domain'>('has-domain');
  const handleSectionState = useCallback((state: 'empty' | 'has-domain') => setSectionState(state), []);
  useEffect(() => { if (!open) setSectionState('has-domain'); }, [open]);
  const isEmptyState = sectionState === 'empty';
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        role: 'dialog',
        'aria-labelledby': 'domain-connect-drawer-title',
        sx: { width: DOMAIN_DRAWER_WIDTH, maxWidth: '100%' },
      }}
    >
      <Box data-testid={LAUNCH_ACTIVATION_TEST_IDS.drawer} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography id="domain-connect-drawer-title" variant="subtitle1" fontWeight={500}>
            Domain
          </Typography>
        </Box>
        <Box sx={{ flex: 1, px: 2.5, py: 2, overflowY: 'auto' }}>
          {open && (
            <CustomDomainSection
              projectId={projectId}
              surface="web"
              addMode="inline"
              onStateChange={handleSectionState}
              suppressGuideAnchors
              verifyButtonTestId={LAUNCH_ACTIVATION_TEST_IDS.drawerVerify}
            />
          )}
        </Box>
        <Box sx={{ px: 2.5, py: 1.75, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button
            fullWidth
            variant={isEmptyState ? 'text' : 'contained'}
            onClick={onClose}
            data-testid={LAUNCH_ACTIVATION_TEST_IDS.drawerClose}
          >
            {isEmptyState ? 'Cancel' : 'Continue'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default DomainConnectDrawer;
